import React, { useState, useEffect, useRef } from "react";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { MdOutlineDelete } from "react-icons/md";
import axiosInstance from "../../Context/axiosInstance";
import { ClipLoader } from "react-spinners";
import ExcelContentImport from "../ExcelContentImport";
import { toast } from "react-toastify";

function ContentModal({
  isModalOpen,
  closeModal,
  type,
  initialContent,
  courseId,
  courseName,
  onSaved,
  imported = false,
}) {
  const [hasImported, setHasImported] = useState(imported);
  const [importOpen, setImportOpen] = useState(false);
  const [newContent, setNewContent] = useState(
    initialContent || [{ title: "", description: "", url: "" }],
  );
  const [selectedCourses, setSelectedCourses] = useState(
    courseId ? [courseId] : [],
  );
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitting = useRef(false);
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });

  const contentLabel = type
    ? type.charAt(0).toUpperCase() + type.slice(1)
    : "Content";

  const typeName = type || "content";
  const pluralType = `${typeName}s`;
  const countLabel = (count) =>
    `${count} ${count === 1 ? typeName : pluralType}`;

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !isSubmitting && !importOpen) closeModal();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [closeModal, isModalOpen, isSubmitting, importOpen]);

  useEffect(() => {
    if (!isModalOpen) return;
    if (courseId) {
      setFilteredCourses([
        { id: courseId, name: courseName || "Current course" },
      ]);
      return;
    }
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axiosInstance.get(
          `/courses?page=1&pageSize=10000&sortColumn=Name&sortDirection=asc&search=${encodeURIComponent(
            searchQuery,
          )}`,
        );
        const fetchedCourses = response.data.response;
        if (active) setFilteredCourses(fetchedCourses || []);
      } catch (error) {
        if (active) setError(error.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [searchQuery, isModalOpen, courseId, courseName]);

  const handleCourseSelect = (courseId) => {
    setSelectedCourses((prevSelectedCourses) => {
      if (prevSelectedCourses.includes(courseId)) {
        return prevSelectedCourses.filter((id) => id !== courseId);
      } else {
        return [...prevSelectedCourses, courseId];
      }
    });
  };

  const handleFormChange = (index, event) => {
    const { name, value } = event.target;
    setNewContent((items) =>
      items.map((item, i) => (i === index ? { ...item, [name]: value } : item)),
    );
    setFormErrors((errors) => ({
      ...errors,
      [`content[${index}].${name}`]: "",
      [`content[${index}].submit`]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError("");
    const errors = {};
    newContent.forEach((item, index) => {
      ["title", "description", "url"].forEach((field) => {
        if (!item[field].trim())
          errors[`content[${index}].${field}`] =
            `${field === "url" ? "URL" : field.charAt(0).toUpperCase() + field.slice(1)} is required.`;
      });
      if (hasImported && item.url.trim()) {
        try {
          const parsed = new URL(item.url.trim());
          if (!["http:", "https:"].includes(parsed.protocol)) throw new Error();
        } catch {
          errors[`content[${index}].url`] = "Enter a valid http or https URL.";
        }
      }
    });
    setFormErrors(errors);
    if (!selectedCourses.length) setError("Please select at least one course.");
    if (!selectedCourses.length || Object.keys(errors).length) return;

    submitting.current = true;
    setIsSubmitting(true);
    setProgress({ completed: 0, total: newContent.length });
    const failedItems = [];
    const failedErrors = {};
    let saved = 0;
    try {
      for (const [index, item] of newContent.entries()) {
        try {
          await axiosInstance.post(`/courses/${type}/courses`, {
            [type]: {
              title: item.title.trim(),
              description: item.description.trim(),
              url: item.url.trim(),
            },
            courseIds: [...selectedCourses],
          });
          saved += 1;
        } catch (err) {
          const data = err.response?.data;
          const reason =
            data?.errors?.[0]?.reason || data?.detail || data?.message;
          const serverError = err.response?.status === 500;
          const message =
            typeof reason === "string"
              ? reason
              : "Couldn't add this item. Please try again.";
          const hint =
            serverError && item.description.includes("!")
              ? ' The description contains "!". Try removing it and retrying; the server did not confirm the cause.'
              : "";
          failedErrors[`content[${failedItems.length}].submit`] =
            `${serverError ? "Internal server error (500). " : ""}${message}${hint}`;
          failedItems.push(item);
        }
        setProgress({ completed: index + 1, total: newContent.length });
      }
      if (saved && onSaved) {
        try {
          await onSaved();
        } catch {
          toast.error(
            "Content was saved, but the list could not refresh. Please refresh the list.",
          );
        }
      }
      if (failedItems.length) {
        toast.error(
          `${saved} of ${countLabel(newContent.length)} added. ${failedItems.length} failed; retry the remaining items.`,
        );
        setNewContent(failedItems);
        setFormErrors(failedErrors);
        setError(
          `${saved} of ${countLabel(newContent.length)} added. ${failedItems.length} couldn't be added. Only the remaining items will be submitted when you retry.`,
        );
      } else {
        toast.success(
          `${countLabel(saved)} added to ${selectedCourses.length} ${selectedCourses.length === 1 ? "course" : "courses"} successfully!`,
        );
        closeModal();
      }
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm overflow-y-auto"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSubmitting)
              closeModal();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-content-modal-title"
            className="relative flex h-[min(820px,calc(100dvh-2rem))] max-h-[calc(100dvh-2rem)] w-[min(1080px,calc(100vw-2rem))] flex-col overflow-hidden rounded-3xl border border-white/70 bg-gradient-to-b from-white to-[#fbfdff] text-gray-900 shadow-[0_28px_75px_rgba(15,23,42,0.28)] dark:border-[var(--study-border)] dark:from-[var(--study-raised)] dark:to-[var(--study-surface)] dark:text-[var(--study-text)] dark:shadow-[0_28px_75px_rgba(2,8,23,0.45)] max-w-full"
          >
            <button
              type="button"
              onClick={closeModal}
              disabled={isSubmitting}
              aria-label="Close modal"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <IoIosCloseCircleOutline className="text-2xl" />
            </button>
            <div className="shrink-0 border-b border-gray-200 pb-4 pl-4 pr-16 pt-5 min-[800px]:pb-5 min-[800px]:pl-6 min-[800px]:pr-20 min-[800px]:pt-6">
              <span className="mb-2 inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#2264E5]">
                {imported ? "Excel import" : "Quick add"}
              </span>
              <h2
                id="quick-content-modal-title"
                className="text-2xl font-bold text-gray-900"
              >
                {imported
                  ? `Review ${pluralType} import`
                  : `Add New ${contentLabel}`}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {courseId
                  ? `Add ${pluralType} to ${courseName || "this course"}.`
                  : `Add ${pluralType} and assign all of them to your selected courses.`}
              </p>
            </div>

            {["video", "material"].includes(type) && (
              <div className="mx-4 mt-3 shrink-0">
                <ExcelContentImport
                  type={type}
                  courseId={courseId}
                  disabled={isSubmitting}
                  onOpen={() => setImportOpen(true)}
                  onClose={() => setImportOpen(false)}
                  onImport={(rows) => {
                    setNewContent((items) => [
                      ...items.filter(
                        (item) => item.title || item.description || item.url,
                      ),
                      ...rows,
                    ]);
                    setHasImported(true);
                    setFormErrors({});
                    setImportOpen(false);
                  }}
                />
              </div>
            )}
            <button
              type="button"
              className="mx-4 mb-0 mt-3 block min-h-[42px] shrink-0 rounded-xl border border-blue-200 px-4 py-2 text-blue-600 min-[800px]:hidden"
              aria-expanded={coursesOpen}
              aria-controls="quick-add-courses"
              style={courseId ? { display: "none" } : undefined}
              onClick={() => setCoursesOpen((open) => !open)}
            >
              Courses ({selectedCourses.length} selected)
            </button>
            <div
              className="relative grid min-h-0 flex-1 grid-cols-1 min-[800px]:grid-cols-[minmax(0,1fr)_370px]"
              style={
                courseId ? { gridTemplateColumns: "minmax(0,1fr)" } : undefined
              }
            >
              <aside
                id="quick-add-courses"
                aria-label="Select courses"
                style={courseId ? { display: "none" } : undefined}
                className={`absolute inset-y-0 right-0 z-20 flex min-h-0 w-[min(340px,100%)] flex-col border-l border-slate-200 bg-slate-50 px-4 py-6 shadow-[-12px_0_30px_rgba(15,23,42,0.15)] transition-[transform,opacity,visibility] duration-200 ease-out motion-reduce:transition-none dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] min-[800px]:visible min-[800px]:static min-[800px]:col-start-2 min-[800px]:row-start-1 min-[800px]:w-auto min-[800px]:translate-x-0 min-[800px]:opacity-100 min-[800px]:shadow-none ${coursesOpen ? "visible translate-x-0 opacity-100" : "invisible translate-x-5 opacity-0"}`}
              >
                <button
                  type="button"
                  onClick={() => setCoursesOpen(false)}
                  className="mb-3 block min-h-[42px] shrink-0 rounded-xl text-blue-600 min-[800px]:hidden"
                >
                  Done selecting courses
                </button>
                {/* Search Bar */}
                <div className="mb-3">
                  <div className="mb-2 flex items-center justify-between">
                    <label
                      htmlFor="quick-content-course-search"
                      className="text-sm font-semibold text-gray-700"
                    >
                      Select courses
                    </label>
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-[#2264E5]">
                      {selectedCourses.length} selected
                    </span>
                  </div>
                  <input
                    disabled={isSubmitting}
                    id="quick-content-course-search"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for courses"
                    className="w-full min-h-[46px] rounded-xl border border-[#dbe3ee] bg-white px-3.5 py-3 text-sm text-gray-900 transition-colors focus:border-[#2264E5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] dark:text-[var(--study-text)] text-left"
                  />
                </div>

                {/* Course Selection with Checkboxes */}
                <div className="relative min-h-[100px] flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-2">
                  {loading ? (
                    <div className="flex justify-center items-center w-full h-full absolute inset-0 z-10">
                      <ClipLoader color="#2264E5" size={50} />
                    </div>
                  ) : filteredCourses.length > 0 ? (
                    filteredCourses.map((course) => (
                      <label
                        key={course.id}
                        className={`mb-1.5 flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 
                          transition-colors 
                          ${
                            selectedCourses.includes(course.id)
                              ? "border-blue-400 bg-blue-100 shadow-[inset_3px_0_#2563eb] [&_span]:text-blue-800 dark:border-blue-400 dark:bg-[#203f63] dark:[&_span]:text-blue-200"
                              : "border-transparent dark:hover:bg-[#264c77] hover:bg-gray-200 hover:shadow-sm"
                          }`}
                      >
                        <input
                          disabled={isSubmitting}
                          type="checkbox"
                          checked={selectedCourses.includes(course.id)}
                          onChange={() => handleCourseSelect(course.id)}
                          className="h-4 w-4 shrink-0 accent-[#2264E5]"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {course.name}{" "}
                          {course.gradeLevel === "Custom" && (
                            <span className="text-red-500">
                              * Custom Course *
                            </span>
                          )}
                        </span>
                      </label>
                    ))
                  ) : (
                    <div className="text-gray-500 absolute inset-0 flex justify-center items-center">
                      No courses found
                    </div>
                  )}
                </div>
              </aside>
              <div className="col-start-1 row-start-1 min-w-0 overflow-y-auto p-4 min-[800px]:p-6">
                <form
                  id="quick-content-form"
                  onSubmit={handleSubmit}
                  noValidate
                  className="flex flex-col gap-3"
                >
                  <p className="text-sm font-semibold text-gray-700">
                    {countLabel(newContent.length)} to add to{" "}
                    {selectedCourses.length}{" "}
                    {selectedCourses.length === 1 ? "course" : "courses"}
                  </p>
                  {newContent.map((content, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-semibold text-gray-700">
                          {contentLabel} {index + 1}
                        </h3>
                        {newContent.length > 1 && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            aria-label={`Remove ${contentLabel} ${index + 1}`}
                            title={`Remove ${contentLabel} ${index + 1}`}
                            onClick={() => {
                              setNewContent((items) =>
                                items.filter((_, i) => i !== index),
                              );
                              setFormErrors({});
                            }}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"
                          >
                            <MdOutlineDelete size={22} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                      {formErrors[`content[${index}].submit`] && (
                        <p role="alert" className="text-sm text-red-600">
                          {formErrors[`content[${index}].submit`]}
                        </p>
                      )}
                      <input
                        type="text"
                        name="title"
                        disabled={isSubmitting}
                        aria-label={`${contentLabel} ${index + 1} title`}
                        aria-invalid={Boolean(
                          formErrors[`content[${index}].title`],
                        )}
                        placeholder={`${contentLabel} Title`}
                        value={content.title}
                        onChange={(e) => handleFormChange(index, e)}
                        className="w-full min-h-[46px] rounded-xl border border-[#dbe3ee] bg-white px-3.5 py-3 text-sm text-gray-900 transition-colors focus:border-[#2264E5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] dark:text-[var(--study-text)]"
                        required
                      />
                      {formErrors[`content[${index}].title`] && (
                        <p className="text-red-500 text-sm">
                          {formErrors[`content[${index}].title`]}
                        </p>
                      )}

                      <input
                        type="text"
                        name="description"
                        disabled={isSubmitting}
                        aria-label={`${contentLabel} ${index + 1} description`}
                        aria-invalid={Boolean(
                          formErrors[`content[${index}].description`],
                        )}
                        placeholder={`${contentLabel} Description`}
                        value={content.description}
                        onChange={(e) => handleFormChange(index, e)}
                        className="w-full min-h-[46px] rounded-xl border border-[#dbe3ee] bg-white px-3.5 py-3 text-sm text-gray-900 transition-colors focus:border-[#2264E5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] dark:text-[var(--study-text)]"
                        required
                      />
                      {formErrors[`content[${index}].description`] && (
                        <p className="text-red-500 text-sm">
                          {formErrors[`content[${index}].description`]}
                        </p>
                      )}

                      <input
                        type="text"
                        name="url"
                        disabled={isSubmitting}
                        aria-label={`${contentLabel} ${index + 1} URL`}
                        aria-invalid={Boolean(
                          formErrors[`content[${index}].url`],
                        )}
                        placeholder={`${contentLabel} URL`}
                        value={content.url}
                        onChange={(e) => handleFormChange(index, e)}
                        className="w-full min-h-[46px] rounded-xl border border-[#dbe3ee] bg-white px-3.5 py-3 text-sm text-gray-900 transition-colors focus:border-[#2264E5] focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] dark:text-[var(--study-text)]"
                        required
                      />
                      {formErrors[`content[${index}].url`] && (
                        <p className="text-red-500 text-sm">
                          {formErrors[`content[${index}].url`]}
                        </p>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() =>
                      setNewContent((items) => [
                        ...items,
                        { title: "", description: "", url: "" },
                      ])
                    }
                    className="rounded-xl border border-dashed border-blue-300 px-4 py-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:opacity-50"
                  >
                    + Add another {type}
                  </button>
                </form>

                {error && (
                  <div
                    role="alert"
                    className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-600"
                  >
                    {error}
                  </div>
                )}
              </div>
            </div>
            <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-white p-4 shadow-[0_-4px_16px_rgba(15,23,42,0.04)] dark:border-[var(--study-border)] dark:bg-[var(--study-surface)] min-[800px]:px-6">
              <button
                onClick={closeModal}
                disabled={isSubmitting}
                className="min-h-[42px] rounded-xl border border-gray-300 bg-white px-5 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
                type="button"
              >
                Cancel
              </button>
              <button
                form="quick-content-form"
                disabled={isSubmitting}
                className="min-h-[42px] min-w-[150px] rounded-xl bg-[#2264E5] px-6 py-2.5 font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                type="submit"
              >
                {isSubmitting ? (
                  <span role="status">
                    Adding {Math.min(progress.completed + 1, progress.total)} of{" "}
                    {progress.total}...
                  </span>
                ) : (
                  `Add ${countLabel(newContent.length)}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ContentModal;
